import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CMSComponents } from '@components/cms';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { Head } from '@components/common/head';
import { useI18NextContext } from '@components/hooks';
import { ResortList } from '@components/resort-list';
import { PageProps } from '@model/pages';
import { Country, Region } from '@model/common';
import { ComponentContainer, SectionSeparator } from '@styles/layout';
import { ResortsBreadcrumb } from '@components/breadcrumbs';
import { Footer } from '@components/footer';
import { DestinationHero } from '@components/common/hero/destination/DestinationHero';
import { Error500 } from '@components/common/hero/error/Error500';
import { ImagesResponse } from '@model/iceberg';
import { DestinationSearchParams, getDestinationSearchParams } from '@util/destination';
import { setPartialSearch } from '@state/deal-finder/dealFinderOperations';
import { getAlgoliaMatchingDestinations } from '@state/algolia/algoliaSelectors';
import { ERROR_CODES } from '@model/common/error-codes';

export interface ResortsPageLayoutProps extends PageProps {
  cmsConfig: any;
  destination: Country | Region;
  hero: ImagesResponse;
}

export const ResortsPageLayout = ({ cmsConfig, destination, hero, promoBannerTripType }: ResortsPageLayoutProps) => {
  const dispatch = useDispatch();
  const t: any = useI18NextContext();
  const { path, display } = destination;

  const algoliaDestinations = useSelector(getAlgoliaMatchingDestinations(path));

  if (!cmsConfig) return <Error500 code={ERROR_CODES.NO_CMS_CONFIG} />;

  useEffect(() => {
    if (algoliaDestinations.length > 0) {
      const defaultSearchParams = getDestinationSearchParams({
        display,
        path,
        destinations: algoliaDestinations
      } as DestinationSearchParams);
      dispatch(setPartialSearch(defaultSearchParams));
    }
  }, [algoliaDestinations]);

  return (
    <>
      <Head metaTitle={t('common__label--resorts-in', { destination: display })} metaDescription={''} />
      <DestinationHero src={hero.images[0]} promoBannerTripType={promoBannerTripType} />
      <ResortsBreadcrumb destination={destination} />
      <SectionSeparator />
      <ComponentContainer>
        <SectionTitle
          size={SectionTitleSizes.LARGE}
          main
          heading={{
            label: 'common__label--resorts-in',
            vars: { destination: display }
          }}
        />
        <ResortList destination={destination} />
      </ComponentContainer>
      <CMSComponents
        components={cmsConfig.components}
        titleSize={SectionTitleSizes.LARGE}
        {...{ destinationName: cmsConfig.title }}
      />
      <SectionSeparator />
      <Footer />
    </>
  );
};
